import { io, type Socket } from 'socket.io-client';
import { getSocketOrigin, shouldUseDevProxy } from '@/lib/dev-url';
import { matchroomLog } from '@/lib/matchroom-logger';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth.store';
import { useSocketStore } from '@/stores/socket.store';
import { useMediaStore } from '@/stores/media.store';

function getSocketUrl(): string {
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const origin = getSocketOrigin();
    matchroomLog.socket('URL resolved', {
      origin,
      proxy: shouldUseDevProxy(),
      hostname: window.location.hostname,
      protocol: window.location.protocol,
    });
    return origin;
  }

  return getSocketOrigin();
}

type RoomSocket = Socket;

let socket: RoomSocket | null = null;
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let lastAuthRefreshAt = 0;
const connectListeners = new Set<() => void>();

const HEARTBEAT_MS = 25_000;
const MAX_RECONNECT_DELAY = 30_000;
const SOCKET_CONNECT_TIMEOUT_MS = 45_000;

function notifySocketConnect(): void {
  for (const listener of connectListeners) {
    try {
      listener();
    } catch {
      // ignore listener errors
    }
  }
}

function ensureRoomSocketInitialized(): void {
  if (socket) return;
  const token = useAuthStore.getState().accessToken;
  if (token) {
    connectRoomSocket(token);
  }
}

/** Subscribe to room socket connect/reconnect (for WebRTC peer recovery). */
export function onRoomSocketConnect(listener: () => void): () => void {
  connectListeners.add(listener);
  if (socket?.connected) {
    listener();
  }
  return () => connectListeners.delete(listener);
}

function getReconnectDelay(attempt: number) {
  return Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY);
}

/** Wait until the room socket is connected (needed before WebRTC signaling). */
export function waitForRoomSocket(timeoutMs = SOCKET_CONNECT_TIMEOUT_MS): Promise<RoomSocket> {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    let connectHandler: (() => void) | null = null;

    const cleanup = () => {
      if (connectHandler && socket) {
        socket.off('connect', connectHandler);
      }
    };

    const finish = (error?: Error) => {
      cleanup();
      if (error) reject(error);
    };

    const tryResolve = (): boolean => {
      if (socket?.connected) {
        finish();
        resolve(socket);
        return true;
      }
      return false;
    };

    ensureRoomSocketInitialized();

    if (tryResolve()) return;

    connectHandler = () => {
      tryResolve();
    };

    let listenerAttached = false;

    const attach = () => {
      ensureRoomSocketInitialized();
      if (!socket || !connectHandler) return;
      if (!listenerAttached) {
        socket.on('connect', connectHandler);
        listenerAttached = true;
      }
      if (!socket.connected) {
        socket.connect();
      }
    };

    attach();

    const poll = window.setInterval(() => {
      if (tryResolve()) {
        clearInterval(poll);
        return;
      }
      attach();
      if (Date.now() >= deadline) {
        clearInterval(poll);
        finish(new Error(socket ? 'SOCKET_CONNECT_TIMEOUT' : 'SOCKET_NOT_INITIALIZED'));
      }
    }, 150);
  });
}

export function getRoomSocket(): RoomSocket | null {
  return socket;
}

export function connectRoomSocket(token: string): RoomSocket {
  if (socket?.connected) {
    socket.auth = { token };
    return socket;
  }

  if (socket) {
    socket.auth = { token };
    const store = useSocketStore.getState();
    store.setStatus('connecting');
    if (!socket.connected) socket.connect();
    return socket;
  }

  const store = useSocketStore.getState();
  store.setStatus('connecting');

  const url = getSocketUrl();
  const pollingOnly = import.meta.env.DEV && shouldUseDevProxy();
  matchroomLog.socket('connecting', { url, path: '/socket.io', pollingOnly });

  socket = io(`${url}/rooms`, {
    path: '/socket.io',
    auth: { token },
    withCredentials: true,
    transports: pollingOnly ? ['polling'] : ['polling', 'websocket'],
    upgrade: !pollingOnly,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: MAX_RECONNECT_DELAY,
  });

  socket.on('connect', () => {
    matchroomLog.socket('connected', { socketId: socket?.id, url });
    useSocketStore.getState().setStatus('connected');
    useSocketStore.getState().resetReconnect();
    useMediaStore.getState().setMediaError(null);
    startHeartbeat();
    notifySocketConnect();
  });

  socket.on('disconnect', (reason) => {
    matchroomLog.socket('disconnected', { reason });
    useSocketStore.getState().setStatus('disconnected');
    stopHeartbeat();
  });

  socket.io.on('reconnect_attempt', (attempt) => {
    matchroomLog.socket('reconnect_attempt', { attempt });
    useSocketStore.getState().setStatus('reconnecting');
    useSocketStore.getState().incrementReconnect();
    socket!.io.opts.reconnectionDelay = getReconnectDelay(attempt);
  });

  socket.io.on('reconnect', () => {
    matchroomLog.socket('reconnected');
    useSocketStore.getState().setStatus('connected');
    useSocketStore.getState().resetReconnect();
    useMediaStore.getState().setMediaError(null);
    startHeartbeat();
    notifySocketConnect();
  });

  socket.on('connect_error', (err) => {
    matchroomLog.socketError('connect_error', err, { url });
    useSocketStore.getState().setStatus('reconnecting');

    const now = Date.now();
    if (now - lastAuthRefreshAt < 10_000) return;
    lastAuthRefreshAt = now;

    void authService
      .refresh()
      .then((result) => {
        const nextToken = result.tokens.accessToken;
        useAuthStore.getState().setAccessToken(nextToken);
        if (!socket) return;
        socket.auth = { token: nextToken };
        socket.connect();
        matchroomLog.socket('token refreshed — reconnecting');
      })
      .catch(() => undefined);
  });

  socket.on('heartbeat', (payload: { timestamp: string }) => {
    useSocketStore.getState().setLastHeartbeat(payload.timestamp);
  });

  return socket;
}

function startHeartbeat() {
  stopHeartbeat();
  heartbeatInterval = setInterval(() => {
    socket?.emit('heartbeat');
  }, HEARTBEAT_MS);
}

function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

export function disconnectRoomSocket() {
  stopHeartbeat();
  if (socket) {
    socket.removeAllListeners();
    socket.io.removeAllListeners();
    socket.disconnect();
  }
  socket = null;
  useSocketStore.getState().setStatus('disconnected');
}

export function emitRoomJoin(roomId: string, payload?: { password?: string; inviteCode?: string }) {
  return new Promise<void>((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Socket not connected'));
      return;
    }
    socket.emit('room:join', { roomId, ...payload });
    socket.once('room:join', () => resolve());
    socket.once('room:error', (err) => reject(new Error(err.error.message)));
  });
}
